import PropTypes from "prop-types";
import React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { Controller } from "react-hook-form";
import { z } from "zod";
import { tradingApi } from "../../services/tradingApi";
import useZodForm from "../form/useZodForm";

const sideOptions = ["buy", "sell"];
const typeOptions = ["market", "limit", "stop", "stop_limit", "trailing_stop"];
const tifOptions = ["day", "gtc", "opg", "cls", "ioc", "fok"];

const orderSchema = z
  .object({
    symbol: z.string().trim().min(1, "Symbol is required"),
    side: z.enum(sideOptions),
    type: z.enum(typeOptions),
    timeInForce: z.enum(tifOptions),
    quantity: z.union([z.number().positive(), z.nan()]).optional(),
    notional: z.union([z.number().positive(), z.nan()]).optional(),
    limitPrice: z.union([z.number().positive(), z.nan()]).optional(),
    stopPrice: z.union([z.number().positive(), z.nan()]).optional(),
    clientOrderId: z.string().max(64, "Client Order Id must be <= 64 characters").optional(),
  })
  .superRefine((value, ctx) => {
    const hasQuantity = value.quantity != null && !Number.isNaN(value.quantity);
    const hasNotional = value.notional != null && !Number.isNaN(value.notional);
    if (!hasQuantity && !hasNotional) {
      ctx.addIssue({ code: "custom", path: ["quantity"], message: "Provide quantity or notional." });
    }
    if (hasQuantity && hasNotional) {
      ctx.addIssue({ code: "custom", path: ["notional"], message: "Provide quantity or notional, not both." });
    }
    if (value.type === "limit" && (value.limitPrice == null || Number.isNaN(value.limitPrice))) {
      ctx.addIssue({ code: "custom", path: ["limitPrice"], message: "Limit price is required for limit orders." });
    }
    if ((value.type === "stop" || value.type === "stop_limit") && (value.stopPrice == null || Number.isNaN(value.stopPrice))) {
      ctx.addIssue({ code: "custom", path: ["stopPrice"], message: "Stop price is required for stop/stop_limit orders." });
    }
  });

const getDefaultValues = (symbol = "") => ({
  symbol,
  side: "buy",
  type: "market",
  timeInForce: "gtc",
  quantity: NaN,
  notional: NaN,
  limitPrice: NaN,
  stopPrice: NaN,
  clientOrderId: "",
});

const OrderModal = ({ open, onClose, defaultSymbol = "", symbolLocked = false, onOrderPlaced }) => {
  const [submitError, setSubmitError] = React.useState("");
  const normalizedSymbol = (defaultSymbol || "").toUpperCase();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useZodForm({
    schema: orderSchema,
    defaultValues: getDefaultValues(normalizedSymbol),
  });

  React.useEffect(() => {
    if (!open) return;
    reset(getDefaultValues(normalizedSymbol));
    if (symbolLocked) {
      setValue("symbol", normalizedSymbol, { shouldValidate: true, shouldDirty: false });
    }
    setSubmitError("");
  }, [open, normalizedSymbol, symbolLocked, reset, setValue]);

  const [placeBuyOrder, { isLoading: isBuying }] = tradingApi.endpoints.placeBuyOrder.useMutation();
  const [placeSellOrder, { isLoading: isSelling }] = tradingApi.endpoints.placeSellOrder.useMutation();
  const isSubmitting = isBuying || isSelling;

  const onSubmit = async (values) => {
    setSubmitError("");

    const payload = {
      symbol: values.symbol,
      type: values.type,
      timeInForce: values.timeInForce,
      quantity: values.quantity && !Number.isNaN(values.quantity) ? Number(values.quantity) : null,
      notional: values.notional && !Number.isNaN(values.notional) ? Number(values.notional) : null,
      limitPrice: values.limitPrice && !Number.isNaN(values.limitPrice) ? Number(values.limitPrice) : null,
      stopPrice: values.stopPrice && !Number.isNaN(values.stopPrice) ? Number(values.stopPrice) : null,
      clientOrderId: values.clientOrderId || null,
    };

    try {
      if (values.side === "sell") {
        await placeSellOrder(payload).unwrap();
      } else {
        await placeBuyOrder(payload).unwrap();
      }
      onOrderPlaced?.();
      onClose();
    } catch (error) {
      setSubmitError(error?.data?.message || "Failed to place order.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>New Order</DialogTitle>
      <DialogContent dividers>
        <Stack component="form" spacing={2} onSubmit={handleSubmit(onSubmit)} sx={{ pt: 1 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap flexWrap="wrap">
            <Controller
              name="symbol"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Symbol"
                  required
                  size="small"
                  disabled={symbolLocked}
                  error={!!errors.symbol}
                  helperText={errors.symbol?.message || (symbolLocked ? "Locked to selected asset." : "")}
                />
              )}
            />
            <Controller
              name="side"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label="Side" size="small">
                  {sideOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.toUpperCase()}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label="Type" size="small">
                  {typeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="timeInForce"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label="Time In Force" size="small">
                  {tifOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.toUpperCase()}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="quantity"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={Number.isNaN(field.value) ? "" : field.value}
                  onChange={(e) => field.onChange(e.target.value === "" ? NaN : Number(e.target.value))}
                  label="Quantity"
                  type="number"
                  size="small"
                  error={!!errors.quantity}
                  helperText={errors.quantity?.message}
                />
              )}
            />
            <Controller
              name="notional"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={Number.isNaN(field.value) ? "" : field.value}
                  onChange={(e) => field.onChange(e.target.value === "" ? NaN : Number(e.target.value))}
                  label="Notional"
                  type="number"
                  size="small"
                  error={!!errors.notional}
                  helperText={errors.notional?.message}
                />
              )}
            />
            <Controller
              name="limitPrice"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={Number.isNaN(field.value) ? "" : field.value}
                  onChange={(e) => field.onChange(e.target.value === "" ? NaN : Number(e.target.value))}
                  label="Limit Price"
                  type="number"
                  size="small"
                  error={!!errors.limitPrice}
                  helperText={errors.limitPrice?.message}
                />
              )}
            />
            <Controller
              name="stopPrice"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={Number.isNaN(field.value) ? "" : field.value}
                  onChange={(e) => field.onChange(e.target.value === "" ? NaN : Number(e.target.value))}
                  label="Stop Price"
                  type="number"
                  size="small"
                  error={!!errors.stopPrice}
                  helperText={errors.stopPrice?.message}
                />
              )}
            />
            <Controller
              name="clientOrderId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Client Order Id"
                  size="small"
                  error={!!errors.clientOrderId}
                  helperText={errors.clientOrderId?.message}
                />
              )}
            />
          </Stack>
          {errors.root?.message ? <FormHelperText error>{errors.root.message}</FormHelperText> : null}
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit(onSubmit)} variant="contained" disabled={isSubmitting}>
          {isSubmitting ? "Placing..." : "Submit Order"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

OrderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  defaultSymbol: PropTypes.string,
  symbolLocked: PropTypes.bool,
  onOrderPlaced: PropTypes.func,
};

export default OrderModal;
